import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Row, Col, Table, Button, Input, Label, FormGroup,
    Spinner, Badge, Alert, ButtonGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../api";
// Round DMS-UAT-3 — Document Control = admin claims OR dms.release module
// (legacy release-window parity; see visibility rules below).
import usePermissions from "../../../../hooks/usePermissions";
import TaskHistory from "../TaskHistory";

/**
 * Round R8 (2026) — ECN Tasks subgrid (Gap 9).
 *
 * Surfaces the legacy DMSNew.vb Tasks subgrid on the modern stack. Restores
 * the ability to attach work-items to an ECN — typically tied to Status 55
 * ("Awaiting Release, Tasks Open") and Status 56 ("Awaiting Release, Tasks
 * Complete"). Round 8 ships the data + UI; auto-status-advance (1 → 55 / 55
 * → 56) is a planned small follow-up.
 *
 * Backend endpoints (DmsController):
 *   GET    /api/dms/{id}/tasks
 *   POST   /api/dms/{id}/tasks                body: { description, dueDate, assignees }
 *   POST   /api/dms/{id}/tasks/{taskId}/complete[?assigneeEmpId=]
 *   DELETE /api/dms/{id}/tasks/{taskId}
 *
 * Visibility rules (mirrored server-side; Round DMS-UAT-3 legacy parity —
 * "Document Control" = doc author's release counterpart: dms.release
 * permission OR isDmsManager OR isItAdmin, matching the WinForms release
 * window where the releaser could assign/remove/complete tasks):
 *   • Anyone authenticated can READ.
 *   • Doc author or Document Control can ADD a task.
 *   • Assignees mark THEIR assignment complete; Document Control can mark
 *     an assignment complete on an assignee's behalf (audited).
 *   • Task creator, doc author, or Document Control can DELETE a task.
 *
 * Props:
 *   dmsId        — the DMS document id this tab belongs to.
 *   docType      — used to gate visibility upstream (ECN-only).
 *   author       — the doc's author empId, used to decide add-task button visibility.
 *   approvals    — passed through unchanged (not currently used inside but
 *                  reserved for future assignee-picker auto-suggestions).
 */
// Round DMS-Legacy-Parity-2 — the legacy ECR task-type list (DMSNew.Designer.vb:2663).
const LEGACY_ECR_TASK_TYPES = [
    "Create Drawing, Router & MAFIA", "Create FAIR", "Create MAFIA", "Create PPAP", "Create Router",
    "Check FAIR/PPAP", "Check QD Table", "Customer Notification", "Dispose Purge Inventory",
    "Rework Inventory", "Supplier Notification", "Update CNC programming",
    "Update Drawing, Router, MAFIA", "Update EPICOR part file", "Update PPAP", "Update QD Table",
    "Update Label Programming", "Update MAFIA", "Update Open Order", "Update Router",
    "Use to Depletion", "Verify MAFIA",
];

export default function EcnTasksTab({ dmsId, docType, author, approvals }) {
    const callerEmpId = useMemo(() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
            return Number(u.uid) || 0;
        } catch { return 0; }
    }, []);

    // Roughly: "can I add or delete tasks?" — we let the server be the
    // authority (it rejects with 403 if wrong), but hiding the buttons
    // keeps the UI clean.
    const isAuthor = Number(author) === callerEmpId;
    const { canAccessModule, isItAdmin: bundleItAdmin, isDmsManager, isSalvie } = usePermissions();
    const claimAdmin = useMemo(() => {
        try {
            const u = JSON.parse(sessionStorage.getItem("authUser") || "{}");
            return Boolean(u.isItAdmin || u.isDmsManager);
        } catch { return false; }
    }, []);
    // Round DMS-UAT-3 — Document Control includes the release-permission
    // holder (dms.release), matching the backend CallerIsDocumentControl()
    // and the legacy release window's task grid privileges.
    // Owner (2026-10-05): the login answer carries these flags in the access bundle, not on
    // authUser, so a DMS Manager without the release permission saw no Edit / Send again
    // although the server allows both. Same set as the backend CallerIsDocumentControl().
    const isAdmin = claimAdmin || bundleItAdmin || isDmsManager || isSalvie || canAccessModule("dms.release");
    const canAddTasks = isAuthor || isAdmin;

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // Add-task form state.
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDescription, setNewDescription] = useState("");
    const [newDueDate, setNewDueDate] = useState(new Date().toISOString().slice(0, 10))   // Round DMS-UAT-23d: Created date, defaults to today;
    const [newAssigneeInput, setNewAssigneeInput] = useState("");
    // Round DMS-UAT-10 (owner): pick assignees from a dropdown of all active
    // employees instead of typing raw EmpIDs — same directory endpoint the
    // HR roster and the EPO form already use.
    const [employeeDir, setEmployeeDir] = useState([]);
    const [newAssignees, setNewAssignees] = useState([]);
    const [adding, setAdding] = useState(false);
    const [addError, setAddError] = useState(null);

    const refresh = useCallback(async () => {
        if (!dmsId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.get(`/dms/${dmsId}/tasks`);
            setTasks(res.data || []);
        } catch (err) {
            setLoadError(err?.response?.data?.error || err?.message || "Failed to load tasks.");
        } finally {
            setLoading(false);
        }
    }, [dmsId]);

    useEffect(() => {
        // Owner decision (2026-09-29): people of the India company help with
        // Florida documents, so they get ECN tasks too. The shared roster holds
        // the Florida employees (unchanged); the India accounts come from the
        // DMS roster (rows marked `india`) and are added to it.
        Promise.allSettled([api.get("WhosInBuilding/userList"), api.get("dms/employees")])
            .then(([roster, dmsRoster]) => {
                const list = roster.status === "fulfilled" && Array.isArray(roster.value?.data)
                    ? [...roster.value.data] : [];
                const known = new Set(list.map((e) => String(e?.empId ?? "").trim()));
                const indiaPeople = dmsRoster.status === "fulfilled" && Array.isArray(dmsRoster.value?.data)
                    ? dmsRoster.value.data.filter((e) => e?.india === true) : [];
                indiaPeople.forEach((e) => {
                    const id = String(e?.empId ?? "").trim();
                    if (id && !known.has(id)) { known.add(id); list.push(e); }
                });
                list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
                setEmployeeDir(list);
            });
        refresh(); }, [refresh]);

    const resetAddForm = () => {
        setNewDescription("");
        setNewDueDate("");
        setNewAssigneeInput("");
        setNewAssignees([]);
        setAddError(null);
    };

    // Dropdown path — same dedupe/validity rules as the old manual entry.
    const handlePickAssignee = (value) => {
        if (!value) return;
        const empId = parseInt(value, 10);
        if (!Number.isFinite(empId) || empId <= 0) return;
        setNewAssignees(prev => prev.includes(empId) ? prev : [...prev, empId]);
    };

    const handleAddAssignee = () => {
        const trimmed = newAssigneeInput.trim();
        if (!trimmed) return;
        const empId = Number(trimmed);
        if (!Number.isFinite(empId) || empId <= 0) {
            setAddError("Enter a valid numeric empId.");
            return;
        }
        if (newAssignees.includes(empId)) {
            setAddError(`empId ${empId} is already in the list.`);
            return;
        }
        setNewAssignees(prev => [...prev, empId]);
        setNewAssigneeInput("");
        setAddError(null);
    };

    const handleRemoveAssignee = (empId) => {
        setNewAssignees(prev => prev.filter(x => x !== empId));
    };

    const handleAddTask = async () => {
        setAddError(null);
        const trimmedDesc = newDescription.trim();
        if (trimmedDesc.length < 3) {
            setAddError("Description must be at least 3 characters.");
            return;
        }
        if (newAssignees.length === 0) {
            setAddError("Add at least one assignee (empId).");
            return;
        }
        setAdding(true);
        try {
            const body = {
                description: trimmedDesc,
                createdDate: newDueDate, dueDate: null || null,
                assignees: newAssignees,
            };
            const res = await api.post(`/dms/${dmsId}/tasks`, body);
            toast.success(res?.data?.message || "Task added.");
            resetAddForm();
            setShowAddForm(false);
            await refresh();
        } catch (err) {
            setAddError(err?.response?.data?.error || err?.message || "Failed to add task.");
        } finally {
            setAdding(false);
        }
    };

    // Round DMS-UAT-8 (2026) — Issue 12 completion: edit a task IN PLACE
    // (description, due date, assignees) instead of delete + re-add. Gated
    // like add/delete (author or Document Control); the backend re-checks,
    // validates assignees against the directory, never removes completed
    // assignments, keeps the 55/56 status coherent, and audits the change.
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [editDescription, setEditDescription] = useState("");
    const [editCreatedDate, setEditCreatedDate] = useState("");   // Round DMS-IssueRound-1c
    const [editAssignees, setEditAssignees] = useState([]);
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState(null);

    const startEditTask = (t) => {
        setEditingTaskId(t.taskId);
        setEditDescription(t.description || "");
        setEditCreatedDate(t.creationDate ? String(t.creationDate).slice(0, 10) : "");
        setEditAssignees((t.assignees || []).map(a => Number(a.empId)));
        setEditError(null);
    };
    const cancelEditTask = () => {
        setEditingTaskId(null);
        setEditError(null);
    };
    // Round DMS-IssueRound-1 (2026) — Issue 4 ROOT CAUSE (frontend half): the
    // edit form's employee dropdown called handlePickAssignee(), which writes
    // to the ADD form's `newAssignees` state — never to `editAssignees` — so
    // "Save task" sent the unchanged assignee list and the server correctly
    // answered "No changes". The edit picker now has its own handler.
    const handlePickEditAssignee = (value) => {
        if (!value) return;
        const empId = parseInt(value, 10);
        if (!Number.isFinite(empId) || empId <= 0) return;
        setEditAssignees(prev => prev.includes(empId) ? prev : [...prev, empId]);
    };
    const removeEditAssignee = (empId) => {
        setEditAssignees(prev => prev.filter(x => x !== empId));
    };
    const saveEditTask = async () => {
        setEditError(null);
        const trimmed = editDescription.trim();
        if (trimmed.length < 3) { setEditError("Description must be at least 3 characters."); return; }
        if (editAssignees.length === 0) { setEditError("At least one assignee is required."); return; }
        setEditSaving(true);
        try {
            const body = {
                description: trimmed,
                // Round DMS-IssueRound-1c — created date is the tracked date;
                // due dates are no longer edited here (left untouched server-side).
                createdDate: editCreatedDate || null,
                assignees: editAssignees,
            };
            const res = await api.put(`/dms/${dmsId}/tasks/${editingTaskId}`, body);
            toast.success(res?.data?.message || "Task updated.");
            setEditingTaskId(null);
            // Round DMS-IssueRound-1 (Issue 4): re-read from the server so the
            // chips show the persisted assignee set (backend is authoritative).
            await refresh();
        } catch (err) {
            setEditError(err?.response?.data?.error || err?.message || "Failed to update task.");
        } finally {
            setEditSaving(false);
        }
    };

    // Owner (2026-10-05, ECN-20611): a rejected task waits for Document Control (or the
    // author) to send it again, to the same person or to someone else.
    const [sendAgainTaskId, setSendAgainTaskId] = useState(null);
    const [sendAgainIds, setSendAgainIds] = useState([]);
    const [sendAgainSaving, setSendAgainSaving] = useState(false);
    const [sendAgainError, setSendAgainError] = useState(null);
    const startSendAgain = (t) => {
        setEditingTaskId(null);
        setSendAgainTaskId(t.taskId);
        setSendAgainIds((t.assignees || []).filter(a => a.rejected && !a.complete).map(a => Number(a.empId)));
        setSendAgainError(null);
    };
    const pickSendAgain = (value) => {
        const empId = parseInt(value, 10);
        if (!Number.isFinite(empId) || empId <= 0) return;
        setSendAgainIds(prev => prev.includes(empId) ? prev : [...prev, empId]);
    };
    const saveSendAgain = async () => {
        setSendAgainError(null);
        if (sendAgainIds.length === 0) { setSendAgainError("Choose who should do the task."); return; }
        setSendAgainSaving(true);
        try {
            const res = await api.post(`/dms/${dmsId}/tasks/${sendAgainTaskId}/send-again`, { assignees: sendAgainIds });
            toast.success(res?.data?.message || "Task sent again.");
            setSendAgainTaskId(null);
            await refresh();
        } catch (err) {
            setSendAgainError(err?.response?.data?.error || err?.message || "Could not send the task again.");
        } finally {
            setSendAgainSaving(false);
        }
    };

    const handleCompleteMyAssignment = async (taskId) => {
        try {
            const res = await api.post(`/dms/${dmsId}/tasks/${taskId}/complete`);
            toast.success(res?.data?.message || "Marked complete.");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Failed to mark complete.");
        }
    };

    // Round DMS-UAT-3 — Document Control marks an assignment complete on the
    // assignee's behalf (legacy release-window Complete checkbox parity).
    // Confirms first (Cancel does nothing); the server re-checks the caller's
    // Document Control claims and writes an "on behalf of" audit entry.
    const handleCompleteOnBehalf = async (taskId, assignee) => {
        const who = assignee.name || assignee.empId;
        if (!window.confirm(`Mark this task complete on behalf of ${who}? This is recorded in the audit trail.`)) return;
        try {
            const res = await api.post(
                `/dms/${dmsId}/tasks/${taskId}/complete`,
                null,
                { params: { assigneeEmpId: assignee.empId } }
            );
            toast.success(res?.data?.message || `Marked complete on behalf of ${who}.`);
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Failed to mark complete.");
        }
    };

    // Round DMS-UAT-21 (user request): e-mail a reminder to the open
    // assignee(s) of a task. Goes to everyone still pending on the task.
    const handleRemind = async (taskId) => {
        try {
            const res = await api.post(`/dms/${dmsId}/tasks/${taskId}/remind`);
            toast.success(res?.data?.message || "Reminder sent.");
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Failed to send reminder.");
        }
    };

    // Round DMS-UAT-21: chips show NAMES (directory lookup) — production
    // showed "3038 (3038)" when the server-side name resolve missed.
    const nameOf = (empId, fallback) => {
        const hit = employeeDir.find((e) => parseInt(e.empId, 10) === parseInt(empId, 10));
        return hit?.name || fallback || String(empId);
    };

    const handleDeleteTask = async (taskId, description) => {
        if (!window.confirm(`Delete task "${description}"? This cannot be undone.`)) return;
        try {
            const res = await api.delete(`/dms/${dmsId}/tasks/${taskId}`);
            toast.success(res?.data?.message || "Task removed.");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.error || err?.message || "Failed to delete task.");
        }
    };

    // If the doc isn't ECN, this tab shouldn't render at all — but the
    // upstream tabs array already gates this, so we keep a small defensive
    // message in case the component is mounted directly.
    if (docType !== 4) {
        return (
            <Alert color="info" className="mb-0">
                Tasks are only available on ECN documents.
            </Alert>
        );
    }

    return (
        <div>
            <div className="d-flex align-items-center gap-2 mb-3">
                <div className="me-auto fw-semibold">
                    <i className="ri-list-check-2 align-bottom me-1 text-primary"></i>
                    ECN Tasks
                    {!loading && tasks.length > 0 && (
                        <Badge color="light" className="ms-2 text-dark">{tasks.length}</Badge>
                    )}
                </div>
                <Button color="light" size="sm" onClick={refresh} disabled={loading} title="Reload">
                    <i className="ri-refresh-line align-bottom"></i>
                </Button>
                {canAddTasks && !showAddForm && (
                    <Button color="primary" size="sm" outline onClick={() => setShowAddForm(true)}>
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Task
                    </Button>
                )}
            </div>

            {showAddForm && (
                <div className="border rounded p-3 mb-3 bg-light-subtle">
                    <Row className="g-2">
                        <Col md={12}>
                            <FormGroup>
                                <Label for="r8_taskDesc" className="small fw-semibold mb-1">Description</Label>
                                {/* Round DMS-Legacy-Parity-2 (audit): legacy DMSNew offered a fixed list of ECR
                                    task types (cboTasks, DMSNew.Designer.vb:2663). Restored as quick-pick chips —
                                    one click fills the description, which stays free text. */}
                                <div className="d-flex flex-wrap gap-1 mb-1">
                                    {LEGACY_ECR_TASK_TYPES.map((t) => (
                                        <Button
                                            key={t}
                                            type="button"
                                            color="light"
                                            size="sm"
                                            className="py-0 px-2"
                                            disabled={adding}
                                            title="Use this legacy ECR task type"
                                            onClick={() => setNewDescription((prev) => (prev && prev.trim() ? prev : t))}
                                        >
                                            {t}
                                        </Button>
                                    ))}
                                </div>
                                <Input
                                    type="textarea"
                                    id="r8_taskDesc"
                                    rows={2}
                                    value={newDescription}
                                    onChange={(e) => setNewDescription(e.target.value)}
                                    placeholder="What needs to be done? (pick a legacy task type above or type your own)"
                                    disabled={adding}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label for="r8_taskDue" className="small fw-semibold mb-1">Created date</Label>
                                <Input
                                    type="date"
                                    id="r8_taskDue"
                                    value={newDueDate}
                                    onChange={(e) => setNewDueDate(e.target.value)}
                                    disabled={adding}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={8}>
                            <FormGroup>
                                <Label for="r8_taskAssignee" className="small fw-semibold mb-1">Assignees</Label>
                                <div className="d-flex gap-2">
                                    <select
                                        className="form-select form-select-sm"
                                        value=""
                                        onChange={(e) => { handlePickAssignee(e.target.value); e.target.value = ""; }}
                                        disabled={adding}
                                    >
                                        <option value="">Select employee to assign…</option>
                                        {employeeDir.map((emp) => (
                                            <option key={emp.empId} value={parseInt(emp.empId, 10)}>
                                                {emp.name} ({emp.empId})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {newAssignees.length > 0 && (
                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                        {newAssignees.map(empId => (
                                            <Badge key={empId} color="info" className="d-inline-flex align-items-center gap-1">
                                                {/* Round DMS-IssueRound-1 (Issue 5): name, never a bare EmpID */}
                                                {nameOf(empId)} ({empId})
                                                <i
                                                    className="ri-close-line"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => handleRemoveAssignee(empId)}
                                                ></i>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>

                    {addError && (
                        <Alert color="danger" className="mb-2 mt-1 py-2">
                            <i className="ri-error-warning-line align-bottom me-1"></i>
                            {addError}
                        </Alert>
                    )}

                    <ButtonGroup>
                        <Button color="light" size="sm" onClick={() => { resetAddForm(); setShowAddForm(false); }} disabled={adding}>
                            Cancel
                        </Button>
                        <Button color="primary" size="sm" onClick={handleAddTask} disabled={adding}>
                            {adding ? (<><Spinner size="sm" /> Adding…</>) : "Add task"}
                        </Button>
                    </ButtonGroup>
                </div>
            )}

            {loading && (
                <div className="text-center py-4">
                    <Spinner color="primary" size="sm" />
                    <div className="text-muted small mt-2">Loading tasks…</div>
                </div>
            )}

            {!loading && loadError && (
                <Alert color="danger" className="mb-0">
                    <i className="ri-error-warning-line align-bottom me-1"></i>
                    {loadError}
                </Alert>
            )}

            {!loading && !loadError && tasks.length === 0 && (
                <div className="text-center py-4 text-muted">
                    <i className="ri-list-check-2" style={{ fontSize: 32 }}></i>
                    <div className="mt-2">No tasks attached to this document yet.</div>
                </div>
            )}

            {!loading && !loadError && tasks.length > 0 && (
                <div className="table-responsive">
                    <Table className="align-middle mb-0" hover>
                        <thead className="table-light">
                            <tr>
                                <th>Task</th>
                                {/* Round DMS-1 (owner request #3): the workflow tracks the
                                    ASSIGNED (created) date, and Days Open makes turnaround
                                    visible for KPIs at a glance. Round DMS-IssueRound-1c
                                    (owner): the Due column is removed — only the created
                                    date is tracked. */}
                                <th>Created</th>
                                <th>Days Open</th>
                                <th>Created by</th>
                                <th>Assignees</th>
                                <th>Progress</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tasks.map(t => {
                                const myAssignment = (t.assignees || []).find(a => Number(a.empId) === callerEmpId);
                                // Owner (2026-10-05): after rejecting a task the person does not get the
                                // tick any more; Document Control sends the task to them again.
                                const canMarkMine  = myAssignment && !myAssignment.complete && !myAssignment.rejected;
                                const canDelete    = isAdmin
                                    || isAuthor
                                    || (t.createdBy && Number(t.createdBy.empId) === callerEmpId);
                                const completedCount = (t.assignees || []).filter(a => a.complete).length;
                                const totalCount     = (t.assignees || []).length;
                                // Tester finding (2026-10-01, ECN-20554): a rejected assignment is
                                // shown as such, with the reason (the task note the rejection wrote),
                                // instead of looking like an open task with progress 0 / 1.
                                const rejectedHere   = (t.assignees || []).filter(a => a.rejected && !a.complete);
                                const rejectionNotes = (t.notes || []).filter(n => n.rejection);
                                const openNotRejected = (t.assignees || []).some(a => !a.complete && !a.rejected);
                                // Owner (2026-10-05): Reopened = its work was sent back; Assigned again =
                                // sent again after a rejection (both until the work is done again).
                                const reopenedHere = !t.allComplete && (t.assignees || []).some(a => a.state === "Reopened");
                                const assignedAgain = !t.allComplete && (t.assignees || []).some(a => a.state === "Assigned again");
                                const isSendingThis = sendAgainTaskId === t.taskId;
                                // Round DMS-UAT-8 — edit-in-place, same gate as add.
                                const canEdit = canAddTasks;
                                const isEditingThis = editingTaskId === t.taskId;
                                return (
                                    <React.Fragment key={t.taskId}>
                                    <tr>
                                        <td style={{ maxWidth: 280 }}>
                                            <div className="fw-medium">{t.description}</div>
                                            <div className="text-muted small">
                                                Added {t.creationDate ? new Date(t.creationDate).toLocaleDateString() : "—"}
                                            </div>
                                            {/* Owner (2026-10-05): the history stays visible for good —
                                                every rejection with its reason, send-backs, send-agains. */}
                                            <TaskHistory events={t.notes} />
                                            {rejectedHere.length > 0 && rejectionNotes.length === 0 && (
                                                <div className="small text-danger mt-1">
                                                    <i className="ri-close-circle-line me-1"></i>
                                                    {`Rejected by ${rejectedHere.map(a => nameOf(a.empId, a.name)).join(", ")}. Send it again or delete it.`}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {(t.assignedDate || t.creationDate)
                                                ? new Date(t.assignedDate || t.creationDate).toLocaleDateString()
                                                : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            {Number.isFinite(Number(t.daysOpen)) ? (
                                                <span className={
                                                    "badge " + (Number(t.daysOpen) >= 30
                                                        ? "bg-danger-subtle text-danger"
                                                        : Number(t.daysOpen) >= 14
                                                            ? "bg-warning-subtle text-warning"
                                                            : "bg-success-subtle text-success")
                                                }>
                                                    {t.daysOpen} {Number(t.daysOpen) === 1 ? "day" : "days"}
                                                </span>
                                            ) : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            {t.createdBy?.name || <span className="text-muted">{t.createdBy?.empId || "—"}</span>}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {(t.assignees || []).map(a => {
                                                    // Round DMS-UAT-3 — legacy release-window parity:
                                                    // Document Control can tick an assignee's Complete
                                                    // (server-validated + audited). Click an incomplete
                                                    // badge to complete on their behalf.
                                                    const canCompleteOnBehalf =
                                                        isAdmin && !a.complete && Number(a.empId) !== callerEmpId;
                                                    const isRejected = Boolean(a.rejected) && !a.complete;
                                                    return (
                                                        <Badge
                                                            key={a.empId}
                                                            color={a.complete ? "success" : (isRejected ? "danger" : "secondary")}
                                                            pill
                                                            style={canCompleteOnBehalf ? { cursor: "pointer" } : undefined}
                                                            onClick={canCompleteOnBehalf
                                                                ? () => handleCompleteOnBehalf(t.taskId, a)
                                                                : undefined}
                                                            title={a.complete
                                                                ? (a.completeStamp ? `Done ${new Date(a.completeStamp).toLocaleDateString()}` : "Done")
                                                                : isRejected
                                                                    ? `Rejected by ${nameOf(a.empId, a.name)} — see the reason under the task. Send it again or delete the task.`
                                                                    : (canCompleteOnBehalf
                                                                        ? `Pending — click to mark complete on behalf of ${nameOf(a.empId, a.name)} (audited)`
                                                                        : "Pending")}
                                                        >
                                                            {a.complete && <i className="ri-check-line me-1"></i>}
                                                            {isRejected && <i className="ri-close-line me-1"></i>}
                                                            {nameOf(a.empId, a.name)}
                                                            <span className="ms-1 opacity-75">({a.empId})</span>
                                                        </Badge>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td>
                                            <Badge color={t.allComplete ? "success" : (rejectedHere.length > 0 ? "danger" : "warning")} pill>
                                                {completedCount} / {totalCount}
                                            </Badge>
                                            {rejectedHere.length > 0 && !t.allComplete && (
                                                <Badge color="danger" pill className="ms-1">Rejected</Badge>
                                            )}
                                            {rejectedHere.length === 0 && reopenedHere && (
                                                <Badge color="warning" pill className="ms-1">Reopened</Badge>
                                            )}
                                            {rejectedHere.length === 0 && !reopenedHere && assignedAgain && (
                                                <Badge color="info" pill className="ms-1">Assigned again</Badge>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <ButtonGroup>
                                                {canMarkMine && (
                                                    <Button
                                                        color="success"
                                                        size="sm"
                                                        outline
                                                        onClick={() => handleCompleteMyAssignment(t.taskId)}
                                                        title="Mark my assignment complete"
                                                    >
                                                        <i className="ri-check-line"></i>
                                                    </Button>
                                                )}
                                                {canEdit && rejectedHere.length > 0 && !isSendingThis && (
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        outline
                                                        onClick={() => startSendAgain(t)}
                                                        title="Send this rejected task again, to the same person or to someone else"
                                                    >
                                                        <i className="ri-user-shared-line"></i>
                                                    </Button>
                                                )}
                                                {canEdit && !isEditingThis && (
                                                    <Button
                                                        color="secondary"
                                                        size="sm"
                                                        outline
                                                        onClick={() => startEditTask(t)}
                                                        title="Edit task (description, created date, assignees)"
                                                    >
                                                        <i className="ri-pencil-line"></i>
                                                    </Button>
                                                )}
                                                {openNotRejected && (
                                                    <Button
                                                        color="warning"
                                                        size="sm"
                                                        outline
                                                        onClick={() => handleRemind(t.taskId)}
                                                        title="E-mail a reminder to the open assignee(s)"
                                                    >
                                                        <i className="ri-mail-send-line"></i>
                                                    </Button>
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        color="danger"
                                                        size="sm"
                                                        outline
                                                        onClick={() => handleDeleteTask(t.taskId, t.description)}
                                                        title="Delete task"
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </Button>
                                                )}
                                            </ButtonGroup>
                                        </td>
                                    </tr>
                                    {/* Round DMS-UAT-8 — inline edit form (Issue 12) */}
                                    {isEditingThis && (
                                        <tr className="table-light">
                                            <td colSpan={7}>
                                                <div className="border rounded p-3 bg-white">
                                                    <Row className="g-2">
                                                        <Col md={12}>
                                                            <Label className="small fw-semibold mb-1">Description</Label>
                                                            <Input
                                                                type="textarea"
                                                                rows={2}
                                                                value={editDescription}
                                                                onChange={(e) => setEditDescription(e.target.value)}
                                                                disabled={editSaving}
                                                            />
                                                        </Col>
                                                        <Col md={4}>
                                                            <Label className="small fw-semibold mb-1">Created date</Label>
                                                            <Input
                                                                type="date"
                                                                value={editCreatedDate}
                                                                onChange={(e) => setEditCreatedDate(e.target.value)}
                                                                disabled={editSaving}
                                                            />
                                                            <div className="text-muted small mt-1">Days Open counts from this date.</div>
                                                        </Col>
                                                        <Col md={8}>
                                                            <Label className="small fw-semibold mb-1">Assignees</Label>
                                                            <div className="d-flex gap-2">
                                                                <select
                                                                    className="form-select form-select-sm"
                                                                    value=""
                                                                    onChange={(e) => { handlePickEditAssignee(e.target.value); e.target.value = ""; }}
                                                                    disabled={editSaving}
                                                                    title="Pick the employee to add; remove the previous assignee with the × on their chip to reassign"
                                                                >
                                                                    <option value="">Select employee to assign…</option>
                                                                    {employeeDir.map((emp) => (
                                                                        <option key={emp.empId} value={parseInt(emp.empId, 10)}>
                                                                            {emp.name} ({emp.empId})
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div className="text-muted small mt-1">
                                                                To reassign: add the new employee, then remove the previous one. Completed assignments stay in the record.
                                                            </div>
                                                            {editAssignees.length > 0 && (
                                                                <div className="d-flex flex-wrap gap-1 mt-2">
                                                                    {editAssignees.map(empId => {
                                                                        const existing = (t.assignees || []).find(a => Number(a.empId) === empId);
                                                                        const done = existing?.complete;
                                                                        return (
                                                                            <Badge key={empId} color={done ? "success" : "info"} className="d-inline-flex align-items-center gap-1">
                                                                                {nameOf(empId, existing?.name)} ({empId})
                                                                                {done
                                                                                    ? <i className="ri-lock-line" title="Already completed — kept in the record"></i>
                                                                                    : <i
                                                                                        className="ri-close-line"
                                                                                        style={{ cursor: "pointer" }}
                                                                                        onClick={() => removeEditAssignee(empId)}
                                                                                        title="Remove assignee"
                                                                                    ></i>}
                                                                            </Badge>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </Col>
                                                    </Row>
                                                    {editError && (
                                                        <Alert color="danger" className="mb-2 mt-2 py-2">
                                                            <i className="ri-error-warning-line align-bottom me-1"></i>
                                                            {editError}
                                                        </Alert>
                                                    )}
                                                    <ButtonGroup className="mt-2">
                                                        <Button color="light" size="sm" onClick={cancelEditTask} disabled={editSaving}>
                                                            Cancel
                                                        </Button>
                                                        <Button color="primary" size="sm" onClick={saveEditTask} disabled={editSaving}>
                                                            {editSaving ? (<><Spinner size="sm" /> Saving…</>) : "Save task"}
                                                        </Button>
                                                    </ButtonGroup>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {isSendingThis && (
                                        <tr className="table-light">
                                            <td colSpan={7}>
                                                <div className="border rounded p-3 bg-white">
                                                    <Label className="small fw-semibold mb-1">Send this task again to</Label>
                                                    <div className="text-muted small mb-2">
                                                        Keep the same person to give it back to them, or remove them and pick someone else.
                                                        They get the task e-mail again.
                                                    </div>
                                                    <select
                                                        className="form-select form-select-sm"
                                                        value=""
                                                        onChange={(e) => { pickSendAgain(e.target.value); e.target.value = ""; }}
                                                        disabled={sendAgainSaving}
                                                        style={{ maxWidth: 420 }}
                                                    >
                                                        <option value="">Add someone…</option>
                                                        {employeeDir.map((emp) => (
                                                            <option key={emp.empId} value={parseInt(emp.empId, 10)}>
                                                                {emp.name} ({emp.empId})
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                                        {sendAgainIds.map(empId => {
                                                            const existing = (t.assignees || []).find(a => Number(a.empId) === empId);
                                                            return (
                                                                <Badge key={empId} color="info" className="d-inline-flex align-items-center gap-1">
                                                                    {nameOf(empId, existing?.name)} ({empId})
                                                                    <i
                                                                        className="ri-close-line"
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => setSendAgainIds(prev => prev.filter(x => x !== empId))}
                                                                        title="Remove"
                                                                    ></i>
                                                                </Badge>
                                                            );
                                                        })}
                                                    </div>
                                                    {sendAgainError && (
                                                        <Alert color="danger" className="mb-2 mt-2 py-2">
                                                            <i className="ri-error-warning-line align-bottom me-1"></i>
                                                            {sendAgainError}
                                                        </Alert>
                                                    )}
                                                    <ButtonGroup className="mt-2">
                                                        <Button color="light" size="sm" onClick={() => setSendAgainTaskId(null)} disabled={sendAgainSaving}>
                                                            Cancel
                                                        </Button>
                                                        <Button color="primary" size="sm" onClick={saveSendAgain} disabled={sendAgainSaving}>
                                                            {sendAgainSaving ? (<><Spinner size="sm" /> Sending…</>) : "Send again"}
                                                        </Button>
                                                    </ButtonGroup>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}
